import { useState, useCallback } from 'react';
import {
  fetchAnimal, listAnimals as listAnimalsQuery, insertAnimal,
  updateAnimal as updateAnimalQuery, fetchRecipientHistory, newAnimalId,
  searchAnimals as searchAnimalsQuery,
} from '../data/animals';
import { currentUserId } from '../data/auth';
import { wasQueued } from '../data/local/queued';

/**
 * Hook para gerenciamento de animais (doadoras e receptoras)
 * Otimizado para cadastro rápido em campo
 */
export const useAnimals = (laboratoryId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Busca animais por termo (autocomplete)
   * @param {string} searchTerm - Termo de busca
   * @returns {Array} Lista de animais
   */
  const searchAnimals = useCallback(async (searchTerm = '') => {
    if (!laboratoryId) return [];

    setLoading(true);
    setError(null);

    try {
      const data = await searchAnimalsQuery(searchTerm, laboratoryId);

      return data || [];
    } catch (err) {
      console.error('Erro ao buscar animais:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  /**
   * Busca animal por ID
   * @param {string} animalId - UUID do animal
   * @returns {Object|null} Dados do animal
   */
  const getAnimalById = useCallback(async (animalId) => {
    if (!animalId) return null;

    setLoading(true);
    setError(null);

    try {
      return await fetchAnimal(animalId);
    } catch (err) {
      console.error('Erro ao buscar animal:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cadastro rápido de animal (apenas campos obrigatórios)
   * @param {Object} animalData - Dados mínimos do animal
   * @param {string} animalData.identifier - Brinco/nome (obrigatório)
   * @param {string} animalData.breed - Raça (obrigatório)
   * @param {string} animalData.client_id - ID do cliente (opcional)
   * @returns {Object|null} Animal criado
   */
  const quickRegister = useCallback(async (animalData) => {
    if (!laboratoryId) {
      setError('Laboratório não identificado');
      return null;
    }

    if (!animalData.identifier || !animalData.breed) {
      setError('Identificação e raça são obrigatórios');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Id gerado aqui: é o que permite devolver a linha ao chamador mesmo
      // quando a escrita ficou na fila, com a mesma identidade que o servidor
      // vai gravar depois.
      const linha = {
        id: newAnimalId(),
        laboratory_id: laboratoryId,
        identifier: animalData.identifier.trim(),
        breed: animalData.breed.trim(),
        client_id: animalData.client_id || null,
        category: animalData.category || null,
        sex: animalData.sex || null,
        rgd: animalData.rgd || null,
        created_by: await currentUserId()
      };
      const resultado = await insertAnimal(linha);
      return wasQueued(resultado) ? linha : resultado;
    } catch (err) {
      console.error('Erro ao cadastrar animal:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  /**
   * Cadastro completo de animal (todos os campos)
   * @param {Object} animalData - Dados completos do animal
   * @returns {Object|null} Animal criado
   */
  const createAnimal = useCallback(async (animalData) => {
    if (!laboratoryId) {
      setError('Laboratório não identificado');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const linha = {
        id: newAnimalId(),
        ...animalData,
        laboratory_id: laboratoryId,
        created_by: await currentUserId()
      };
      const resultado = await insertAnimal(linha);
      return wasQueued(resultado) ? linha : resultado;
    } catch (err) {
      console.error('Erro ao criar animal:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  /**
   * Atualiza dados de um animal
   * @param {string} animalId - UUID do animal
   * @param {Object} updates - Campos a atualizar
   * @returns {Object|null} Animal atualizado
   */
  const updateAnimal = useCallback(async (animalId, updates) => {
    if (!animalId) return null;

    setLoading(true);
    setError(null);

    try {
      return await updateAnimalQuery(animalId, updates, laboratoryId);
    } catch (err) {
      console.error('Erro ao atualizar animal:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
    // `laboratoryId` entra nas dependências: sem ele a função guardaria o
    // valor da primeira renderização, e a emenda iria para a lista errada
    // depois de trocar de laboratório.
  }, [laboratoryId]);

  /**
   * Lista todos os animais do laboratório
   * @param {Object} filters - Filtros opcionais
   * @returns {Array} Lista de animais
   */
  const listAnimals = useCallback(async (filters = {}) => {
    if (!laboratoryId) return [];

    setLoading(true);
    setError(null);

    try {
      return await listAnimalsQuery(laboratoryId, filters) || [];
    } catch (err) {
      console.error('Erro ao listar animais:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  /**
   * Obtém histórico de transferências de uma receptora
   * @param {string} animalId - UUID do animal (receptora)
   * @returns {Object} Estatísticas e histórico
   */
  const getRecipientHistory = useCallback(async (animalId) => {
    if (!animalId) return null;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchRecipientHistory(animalId);

      // Calcular estatísticas
      const total = data.length;
      const pregnant = data.filter(e =>
        e.pregnancy_status === 'pregnant_30d' ||
        e.pregnancy_status === 'pregnant_60d' ||
        e.pregnancy_status === 'born'
      ).length;
      const successRate = total > 0 ? Math.round((pregnant / total) * 100) : 0;

      return {
        total_transfers: total,
        total_pregnancies: pregnant,
        success_rate: successRate,
        transfers: data
      };
    } catch (err) {
      console.error('Erro ao buscar histórico:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    searchAnimals,
    getAnimalById,
    quickRegister,
    createAnimal,
    updateAnimal,
    listAnimals,
    getRecipientHistory
  };
};
